/**
 * Utility for copying content to clipboard in multiple formats
 * Supports both rich text (HTML) and plain text for maximum compatibility
 */

import { marked } from 'marked';
import DOMPurify from 'dompurify';

/**
 * Configure marked for safe HTML rendering
 */
const configureMarked = () => {
  marked.setOptions({
    gfm: true,
    breaks: true,
  });
};

// Initialize marked configuration
configureMarked();

/**
 * Convert markdown to clean HTML suitable for clipboard
 */
function markdownToHtml(markdown: string): string {
  // Parse markdown to HTML
  const rawHtml = marked.parse(markdown) as string;
  
  // Sanitize the HTML to prevent XSS
  const cleanHtml = DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'b', 'i', 'u', 's', 'del',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'blockquote', 'pre', 'code',
      'a', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'hr', 'div', 'span', 'img', 'sup', 'sub'
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'title', 'class']
  });
  
  return cleanHtml;
}

/**
 * Convert markdown to plain text by stripping formatting
 * Preserves structure (headings, lists) but removes markdown syntax
 */
function markdownToPlainText(markdown: string): string {
  let text = markdown;
  
  // Remove code blocks but keep content
  text = text.replace(/```[\s\S]*?```/g, (match) => {
    const content = match.replace(/```\w*\n?/g, '').replace(/```$/g, '');
    return content.trim();
  });
  
  // Remove inline code backticks
  text = text.replace(/`([^`]+)`/g, '$1');
  
  // Convert headers to plain text with newlines
  text = text.replace(/^#{1,6}\s+(.+)$/gm, '$1');
  
  // Remove bold/italic markers
  text = text.replace(/\*\*([^*]+)\*\*/g, '$1');
  text = text.replace(/\*([^*]+)\*/g, '$1');
  text = text.replace(/__([^_]+)__/g, '$1');
  text = text.replace(/_([^_]+)_/g, '$1');
  
  // Convert links to just text (or text with URL)
  text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)');
  
  // Convert unordered list markers
  text = text.replace(/^\s*[-*+]\s+/gm, '• ');
  
  // Convert ordered list markers (keep numbers)
  text = text.replace(/^\s*(\d+)\.\s+/gm, '$1. ');
  
  // Remove blockquote markers
  text = text.replace(/^\s*>\s*/gm, '');
  
  // Remove horizontal rules
  text = text.replace(/^[-*_]{3,}$/gm, '─'.repeat(40));
  
  // Clean up excessive newlines
  text = text.replace(/\n{3,}/g, '\n\n');
  
  return text.trim();
}

/**
 * Copy text to clipboard in multiple formats (HTML + plain text)
 * This provides the best experience across different paste targets:
 * - Rich text apps (Word, Outlook, etc.) get formatted HTML
 * - Plain text apps (Notepad, code editors) get clean text
 * 
 * @param markdown - The markdown text to copy
 * @returns Promise<boolean> - true if copy succeeded, false otherwise
 */
export async function copyRichText(markdown: string): Promise<boolean> {
  try {
    // Convert to both formats
    const html = markdownToHtml(markdown);
    const plainText = markdownToPlainText(markdown);
    
    // Wrap HTML in a complete document fragment for better paste compatibility
    const wrappedHtml = `<html><body>${html}</body></html>`;
    
    // Create clipboard items with both formats
    const htmlBlob = new Blob([wrappedHtml], { type: 'text/html' });
    const textBlob = new Blob([plainText], { type: 'text/plain' });
    
    // Use the modern Clipboard API with multiple formats
    const clipboardItem = new ClipboardItem({
      'text/html': htmlBlob,
      'text/plain': textBlob
    });
    
    await navigator.clipboard.write([clipboardItem]);
    return true;
  } catch (error) {
    console.error('Failed to copy rich text:', error);
    
    // Fallback to simple text copy if ClipboardItem is not supported
    try {
      const plainText = markdownToPlainText(markdown);
      await navigator.clipboard.writeText(plainText);
      return true;
    } catch (fallbackError) {
      console.error('Fallback copy also failed:', fallbackError);
      return false;
    }
  }
}

/**
 * Copy plain markdown text to clipboard (original behavior)
 * Use this when you want to preserve the markdown formatting
 * 
 * @param text - The text to copy as-is
 * @returns Promise<boolean> - true if copy succeeded, false otherwise
 */
export async function copyPlainText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Failed to copy text:', error);
    return false;
  }
}

/**
 * Copy code to clipboard (for code blocks)
 * Always copies as plain text to preserve formatting
 * 
 * @param code - The code to copy
 * @returns Promise<boolean> - true if copy succeeded, false otherwise
 */
export async function copyCode(code: string): Promise<boolean> {
  return copyPlainText(code);
}
